import React, { useState } from "react";
import { FaGoogle, FaEye, FaEyeSlash } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FaCircleChevronLeft } from "react-icons/fa6";
import toast from "react-hot-toast";

const SignIn = () => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;
  const queryClient = useQueryClient();

  const { mutate: signIn, isPending, isError, error } = useMutation({
    mutationKey: ["auth"],
    mutationFn: async ({ email, password }) => {
      const res = await fetch(`${API_URL}/auth/signin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "something went wrong");

      Cookies.set("gameport", data.token, {
        expires: 15,
        secure: process.env.NODE_ENV !== "development",
        sameSite: "Strict",
      });

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auth"] });
      toast.success("Logged in successfully");
      navigate("/");
    },
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  const googleAuth = () => {
    window.open(`${API_URL}/auth/google/callback`, "_self");
  };

  const validate = () => {
    const e = {};
    if (!formData.email.trim()) e.email = "Email is required";
    if (!formData.password) e.password = "Password is required";
    else if (formData.password.length < 8)
      e.password = "Password must be at least 8 characters";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    signIn(formData);
  };

  return (
    <div className="min-h-screen w-full bg-slate-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8">
        <div className="text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900">
            Sign In
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Sign in to your account
          </p>
        </div>

        <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
          {/* Email */}
          <div>
            <label className="text-sm font-semibold text-gray-800">
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="you@example.com"
              className="w-full mt-1 px-4 py-3 rounded-xl border border-gray-200 text-gray-900 outline-none
                         focus:border-black focus:ring-2 focus:ring-black/10"
            />
            {errors.email && (
              <p className="text-red text-xs mt-1 font-medium">{errors.email}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="text-sm font-semibold text-gray-800">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="********"
                className="w-full mt-1 px-4 py-3 pr-12 rounded-xl border border-gray-200 text-gray-900 outline-none
                           focus:border-black focus:ring-2 focus:ring-black/10"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-700 hover:text-black"
                aria-label="Toggle password visibility"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {errors.password && (
              <p className="text-red text-xs mt-1 font-medium">
                {errors.password}
              </p>
            )}
          </div>

          {/* Remember + Forgot */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                name="rememberMe"
                onChange={handleChange}
                className="h-4 w-4 accent-red"
              />
              <span className="text-sm text-gray-600">Remember me</span>
            </label>

            <Link to="/forgot-password" className="text-sm text-red hover:underline">
              Forgot password?
            </Link>
          </div>

          {/* Signup */}
          <p className="text-sm text-gray-700">
            Don&apos;t have an account?{" "}
            <Link to="/signup" className="text-red font-semibold hover:underline">
              Sign Up
            </Link>
          </p>

          {/* Error */}
          {isError && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
              <p className="text-red text-sm font-semibold text-center">
                {error.message}
              </p>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={isPending}
            className={`w-full py-3 rounded-xl font-semibold transition
              ${isPending ? "bg-gray-300 text-gray-700 cursor-not-allowed" : "bg-red text-white hover:opacity-90"}`}
          >
            {isPending ? "Signing In..." : "Sign In"}
          </button>
        </form>

        {/* Divider */}
        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="text-sm text-gray-500">Or continue with</span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>

        {/* Social */}
        {/* <button
          type="button"
          onClick={googleAuth}
          className="w-full border border-gray-200 py-3 rounded-xl flex items-center justify-center gap-2 hover:bg-gray-50 transition font-semibold"
        >
          <FaGoogle className="text-red" /> Google
        </button> */}

        {/* Back */}
        <div className="mt-6 flex justify-center">
          <Link to="/" className="inline-flex items-center gap-2 text-red hover:underline font-semibold">
            <FaCircleChevronLeft className="text-red" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
